import Minze, { Attrs, EventListeners, MinzeElement } from 'minze'
import { Font } from '../../../constants'
import { Locale } from '../../../modules/locale'
import { ConsoleInfo } from '../../../modules/logging'
import MainModule from '../../../modules/main'
import { ISearchConfig } from '../../../modules/search'
import { SearchBarSubscriber } from '../../../modules/search/SearchBarSubscriber'
import { isElementVisible } from '../../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'searchDesktopWrapper', {
  search: 'Rechercher',
  searchButton: 'Chercher',
  searchPlaceholder: 'Rechercher',
  filters: 'Filtres:',
  clearFilter: 'Réinitialiser les filtres',
})

Locale.getInstance().registerKeySet('en', 'searchDesktopWrapper', {
  search: 'Search',
  searchButton: 'Search',
  searchPlaceholder: 'Search for an event, artist or venue',
  filters: 'Filters:',
  clearFilter: 'Erase',
})

export interface LvcSearchBarWrapper {
  dataGroupId?: string
  dataElementId?: string
  dataRedirectToSearch?: boolean
}

export class LvcSearchBarWrapper extends MinzeElement {
  attrs: Attrs = ['data-group-id', 'data-element-id', ['data-redirect-to-search', true]]
  query: string = ''
  searchBarSubscriber: SearchBarSubscriber | null = null
  hasAnyFacets: boolean | undefined = false
  public async handleSubscriberUpdate(searchConfig: ISearchConfig): Promise<void> {
    if (!isElementVisible(this)) {
      ConsoleInfo(true, `NOT Handling subscriber update for ${this.dataElementId}`, this)
      return
    }

    ConsoleInfo(true, `Handling subscriber update for ${this.dataElementId}`, this)

    this.hasAnyFacets = await this.searchBarSubscriber?.hasAnyFilters()
    this.rerender()
  }

  executeSearch = async (event?: Event) => {
    Minze.cast('lvc:search-menu-button-clicked')
    // Ensure the latest input is flushed before executing search
    const searchBarEl = this.select('lvc-search-bar') as any
    // If this was triggered by the global Enter event, ignore it unless this wrapper's input is focused
    const isGlobalEnter = event?.type === 'lvc:search-execution-requested'
    if (isGlobalEnter) {
      const inputEl = searchBarEl?.select?.('[name="query"]') as HTMLInputElement | null
      const isFocused =
        Boolean(searchBarEl?.focused) || (inputEl && document.activeElement === inputEl)
      if (!isFocused) {
        return
      }
    }
    if (searchBarEl?.flushQueryAndExecute) {
      await searchBarEl.flushQueryAndExecute()
    } else {
    }
    this.searchBarSubscriber?.goToSearch(this.dataRedirectToSearch, true)
  }

  clearAll = () => {
    this.searchBarSubscriber?.clearAll(false)
  }

  onReady(): void | Promise<void> {
    //@TODO replace with real config
    this.searchBarSubscriber = new SearchBarSubscriber(
      this.dataGroupId as string,
      this.handleSubscriberUpdate.bind(this)
    )
    const mainModule = MainModule.getInstance()
    mainModule.getSearchManager().subscribe(this.searchBarSubscriber)
  }

  html = () => {
    return /*html*/ `
    <div class="lvc-search-container">
      <div class="search-input-wrapper">
        <lvc-search-bar></lvc-search-bar>
      </div>
      <div class="search-filters-wrapper">
        <lvc-region-search></lvc-region-search>
        <lvc-dates-search></lvc-dates-search>
        <lvc-disciplines-search></lvc-disciplines-search>
        <lvc-audiences-search></lvc-audiences-search>
      </div>
      <div class="search-buttons-wrapper">
        <button name="clearFacets-mobile" type="button" data-available="${this.hasAnyFacets}" class="clear-filters-mobile">
          <lvc-reset-search-icon></lvc-reset-search-icon>
        </button>
        <button id="search-button" type="button">
          <span class="search-text">${Locale.getKey('searchDesktopWrapper.search')}</span>
          <lvc-search-alternative-icon width="18" height="18"></lvc-search-alternative-icon>
        </button>
        <button name="clearFacets" type="button" data-available="${this.hasAnyFacets}" class="clear-filters">
          <lvc-reset-search-icon></lvc-reset-search-icon>
        </button>
      </div>     
    </div>
    `
  }

  css = () => `
  :host {
    width: 100%;
    background: rgba(255, 255, 255, 0.5);
    padding: 15px 2rem;
    border-bottom: 1px solid #ffffff;
    height: 100%;
  }
  
  #search-button {
    cursor: pointer;
    background: #1E007D;
    border-radius: 100%;
    border: none;
    height: 54px;
    width: 54px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
  }

  .search-filters-wrapper {
    display: flex;
    background-color: white;
    border-radius: 999px;
    align-items: center;
    height: 54px;
  }

  lvc-search-bar {
    border-radius: 999px;
    background-color: white;
    border: none;
    flex: 1;
    width: 100%;
    flex-grow: 1;
  }

  .lvc-search-container {
    display: flex;
    gap: 1rem;
    max-width: 80rem;
    width: 100%;
    margin: 0 auto;
    flex-direction: row;
    justify-content: center;
    height: 100%;
  }

  [name="clearFacets"], [name="clearFacets-mobile"] {
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--brand-minuit, #1E007D);
    text-align: right;
    font-variant-numeric: lining-nums tabular-nums;
    font-family: ${Font.family};
    font-size: 14px;
    font-style: normal;
    font-weight: ${Font.weight.medium};
    line-height: 120%;
    text-decoration-line: underline;
  }

  .search-buttons-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;
  }

  .search-text {
    display: none;
    color: white;
    font-size: 16px;
    margin-left: 10px;
  }

  [name="clearFacets"][data-available="true"] {
    display: block;
  }


  @media screen and (max-width: 991px) {

    .lvc-search-container {
      overflow-y: auto;
      height: 100%; 
      justify-content: flex-start;
      flex-direction: column;
      width: 100%; 
      gap: 0;
    }

    .search-filters-wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      height: auto;
      padding: 15px;
    }

    .search-filters-wrapper > * {
      width: 100%;
    }

    .search-input-wrapper {
      padding: 15px;
    }

    .clear-filters-mobile {
      display: flex;
      width: 100%;
      padding: 0;
      flex: 0;
    }

    [name="clearFacets"][data-available="true"] {
    display: none;
  }

    lvc-search-alternative-icon {
      display: flex;
    }

    #search-button {
      display: flex;
      flex-grow: 1; 
      width: auto; 
      border-radius: 51px;
      gap: 10px;
      height: 40px;
      font-family: inherit;
    }

    .search-text {
      display: inline-block; 
      margin-left: 10px;
      font-weight: 500;
      font-size: 12px;
    }

    .lvc-search-container {
      height: 100%; 
      justify-content: flex-start;
      flex-direction: column;
    }

    .search-buttons-wrapper {
      z-index: 1000;
      box-shadow: 0px -2px 5px 0px rgba(0, 0, 0, 0.05);
      padding: 20px 15px;
      margin-top: auto;
      justify-content: space-between;
      display: flex;
      gap: 10px;
    }

    .search-filters-wrapper > * {
      border-bottom: 1px solid var(--Ciel, #BFF0FF);
      margin-bottom: 10px;
    }

    :host {
     padding: 0;
    }
  }
`

  eventListeners: EventListeners = [
    ['#search-button', 'click', this.executeSearch],
    ['[name="clearFacets"]', 'click', this.clearAll],
    ['[name="clearFacets-mobile"]', 'click', this.clearAll],
    [window, 'lvc:search-execution-requested', this.executeSearch],
  ]
}
